<template>
  <MembershipPage v-if="cancelErrors?.length > 0">
    <template #aboveBox>
      <p
        v-for="error in cancelErrors"
        :key="error">
        {{ error }}
      </p>
    </template>
  </MembershipPage>
  <MembershipPage title="It's Hard To Say Goodbye">
    <template #aboveBox>
      <div class="d-flex flex-column align-items-center gap-lgp">
        <p>Please let us know the main reason you’ve decided to cancel.</p>
        <CancelReasonsDropdown 
          :cancel-reason="cancelReason"
          @update:primary="onPrimaryUpdate"
          @update:secondary="onSecondaryUpdate"/>
      </div>
    </template>
    <template
      v-if="boxContent"
      #boxContent>
      <div class="d-flex flex-column gap-sm align-items-center">
        <p v-html="boxContent.textPrimary"/>
        <BaseButton
          :text="boxContent.actionTextPrimary"
          tracking-name="Cancel Reason Action"
          :tracking-data="{
            action: 'primary',
            primaryReason: getPrimaryReasonText(),
            secondaryReason: getSecondaryReasonText(),
            actionText: boxContent.actionTextPrimary
          }"
          @click="boxContent.actionPrimary"/>
        <p
          v-if="boxContent.textSecondary"
          v-html="boxContent.textSecondary"/>
        <BaseButton
          v-if="boxContent.textSecondary"
          :text="boxContent.actionTextSecondary"
          tracking-name="Cancel Reason Action"
          :tracking-data="{
            action: 'secondary',
            primaryReason: getPrimaryReasonText(),
            secondaryReason: getSecondaryReasonText(),
            actionText: boxContent.actionTextSecondary
          }"
          @click="boxContent.actionSecondary"/>
      </div>
    </template>
    <template #belowBox>
      <div class="d-flex flex-column gap-sm align-items-center">
        <BaseTextArea
          v-if="cancelReason.reasonChoicePrimary?.id && cancelReason.reasonChoiceSecondary?.id"
          v-model="cancelReason.feedback"
          placeholder="Please provide additional details as to why you are canceling. *required"
          rows="4"
          class="w-100"
          @input="onFeedbackUpdate"/>
        <BaseButton
          :text="isCancelling ? 'Cancelling...' : 'Cancel Membership'"
          :disabled="isCancelling || !cancelFormValid"
          @click="onCancelMembership"/>
        <BaseButton
          variant="text-link"
          text="I Want to Keep My Membership"
          :disabled="isCancelling"
          :to="{ name: 'account' }"/>
      </div>
    </template>
  </MembershipPage>
</template>

<script setup>
import MembershipPage from '@/components/account/membership/new/MembershipPage.vue'
import BaseButton from '@shared/components/ADORN/BaseButton.vue'
import CancelReasonsDropdown from '@/components/account/membership/new/CancelReasonsDropdown.vue'
import BaseTextArea from '@/components/global/BaseTextArea.vue'
import { onMounted, computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useAccountStore } from '@/stores/account.js'
import { useClientStore } from '@shared/stores/client.js'
import { usePauseCancel } from '@/composables/pauseCancel.js'
import useAnalytics from '@shared/composables/analytics.js'
import { storeToRefs } from 'pinia'

const accountStore = useAccountStore()
const { cancelSubscription } = accountStore
const {  getBilling, cancelReasons, getCancelReasons, boxContentToReasonMap } = usePauseCancel()
const router = useRouter()
const { track } = useAnalytics()
const clientStore = useClientStore()
const { plans } = storeToRefs(clientStore)
const cancelReason = ref({
  reasonChoicePrimary: {
    id: null
  },
  reasonChoiceSecondary: {
    id: null
  },
  feedback: ''
})

const cancelErrors = ref([])
const isCancelling = ref(false)

const cancelFormValid = computed(() => {
  const hasPrimary = !!cancelReason.value.reasonChoicePrimary?.id
  const hasFeedback = cancelReason.value.feedback !== ''
  
  if (hasPrimary) {
    const selectedPrimary = cancelReasons.value.find(r => r.id === cancelReason.value.reasonChoicePrimary?.id)
    const hasSecondaryReasons = selectedPrimary?.secondaryReasons && selectedPrimary.secondaryReasons.length > 0
    if (hasSecondaryReasons) {
      if (hasFeedback) {
        return true
      } else {
        return false
      }
    } else {
      return true
    }
  }
  return false
})

const onPrimaryUpdate = (value) => {
  cancelReason.value.reasonChoicePrimary = { id: value }
}

const onSecondaryUpdate = (value) => {
  cancelReason.value.reasonChoiceSecondary = { id: value }
}

const onFeedbackUpdate = (value) => {
  cancelReason.value.feedback = value
}

const onCancelMembership = async () => {
  cancelErrors.value = []
  isCancelling.value = true
  
  try {
    await cancelSubscription(cancelReason.value)
    await getBilling()
    router.push({ name: 'cancel-confirmation' })
  } catch (error) {
    cancelErrors.value = error
  } finally {
    isCancelling.value = false
  }
}

onMounted(async () => {
  track('Viewed Cancel Membership', { currentPlan: plans.value?.currentBasePlan?.displayName })
  await getBilling()
  if (!cancelReasons.value.length) {
    await getCancelReasons()
  }
})

const boxContent = computed(() => {
  const primaryId = cancelReason.value.reasonChoicePrimary?.id || null
  const secondaryId = cancelReason.value.reasonChoiceSecondary?.id || null
  if (!primaryId || !secondaryId) {
    return null
  }
  return boxContentToReasonMap.value[primaryId][secondaryId]
})

const getPrimaryReasonText = () => {
  const primaryId = cancelReason.value.reasonChoicePrimary?.id
  if (!primaryId) return null
  const reason = cancelReasons.value.find(r => r.id === primaryId)
  return reason?.reason || null
}

const getSecondaryReasonText = () => {
  const primaryId = cancelReason.value.reasonChoicePrimary?.id
  const secondaryId = cancelReason.value.reasonChoiceSecondary?.id
  if (!primaryId || !secondaryId) return null
  const primaryReason = cancelReasons.value.find(r => r.id === primaryId)
  if (!primaryReason?.secondaryReasons) return null
  const secondaryReason = primaryReason.secondaryReasons.find(r => r.id === secondaryId)
  return secondaryReason?.reason || null
}

</script>

<style scoped>
li {
  list-style-position: inside;
}
</style>
